/**
 * Pure data shaping for the test-runs trend chart: turns the
 * `GET /workflows/test-executions/trend` response into per-bar chart rows
 * plus the stable, ordered list of diagnosis-category segments to render.
 *
 * Kept separate from the chart component (`TestRunsTrendChart.tsx`) because
 * the component needs theme-aware colors (a runtime concern); this module has
 * none of that and is unit-testable in isolation.
 *
 * @module ui/pages/runs/test-runs/testRunsTrendChartData
 */
import {
  FAILURE_DIAGNOSTIC_CATEGORIES,
  type FailureDiagnosticCategory,
} from '@chatsdet/types/issues';
import type { RunTarget } from '@chatsdet/types';

/** One point in the `GET /workflows/test-executions/trend` response. */
export type TestRunsTrendPoint = {
  kind: 'cofactor' | 'external';
  executionId: string;
  startedAt: string;
  status: string;
  runTarget: RunTarget;
  passed: number;
  flaky: number;
  failedTotal: number;
  failedByCategory: Record<string, number>;
  other: number;
  totalWorkflows: number;
};

/** Bucket for failed flows with no completed diagnostic explaining them. */
export const UNDIAGNOSED_KEY = 'undiagnosed';
/** Skipped / degraded-and-not-run / canceled / still-unresolved flows. */
export const OTHER_KEY = 'other';
/** Folded remainder of diagnosis categories beyond {@link MAX_DIAGNOSIS_CATEGORIES}. */
export const OTHER_DIAGNOSES_KEY = 'otherDiagnoses';
/**
 * Marks a padding slot with no run in it. Carries a value of 1 purely so the
 * slot draws a baseline tick instead of nothing — the strip then reads as a
 * fixed-width sparkline whose history happens to be empty, rather than as a
 * chart that failed to render. Always 0 on real rows.
 */
export const PLACEHOLDER_KEY = 'placeholder';

/**
 * A stacked bar has room for a handful of distinguishable fills before color
 * stops being a usable encoding — see the `dataviz` skill's validated
 * categorical palette (`TestRunsTrendChart.tsx`), which ships exactly this
 * many hues. Categories beyond the top 4 by window volume fold into
 * {@link OTHER_DIAGNOSES_KEY} rather than growing the segment count.
 */
export const MAX_DIAGNOSIS_CATEGORIES = 4;

/**
 * Slots the sparkline always renders, whether or not there are that many runs.
 *
 * Bar width is the strip's width divided by the number of bars, so letting the
 * count float means six runs render as six marks marooned in whitespace and
 * sixty render as a dense strip — the same data reading as two different
 * charts. Fixing the count fixes the rhythm: bars sit shoulder to shoulder at
 * every volume, and a sparse window is legible *as* sparse.
 */
export const TREND_SLOT_COUNT = 60;

/**
 * One bar's data. The top diagnosis-category counts live as dynamic
 * top-level keys — one per entry in `categoryKeys`, see
 * {@link buildTestRunsTrendChartData} — so `StackedBarChart`'s `dataKey`-driven
 * stacks can read them directly. The index signature exists only to carry
 * those dynamic keys; every field the module itself sets is listed explicitly.
 */
export type TestRunsTrendChartRow = {
  kind: TestRunsTrendPoint['kind'] | null;
  executionId: string;
  date: string;
  runTargetKey: string;
  runTargetLabel: string;
  status: string;
  totalWorkflows: number;
  passed: number;
  flaky: number;
  undiagnosed: number;
  /** Sum of every folded category's count on this bar. */
  otherDiagnoses: number;
  /** Only the folded categories present (>0) on this bar — for the tooltip. */
  otherDiagnosesBreakdown: Partial<Record<FailureDiagnosticCategory, number>>;
  other: number;
  /** 1 on a padding slot, 0 on a real run — see {@link PLACEHOLDER_KEY}. */
  placeholder: number;
  [key: string]: unknown;
};

function formatTrendDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Builds chart rows plus the stable, ranked list of diagnosis categories to
 * render as their own segment, and the (also stable) list folded into
 * {@link OTHER_DIAGNOSES_KEY}.
 *
 * Ranking is computed once, by total volume across the whole window, and then
 * applied uniformly to every bar — a category's slot (and therefore its
 * color, assigned by index in the caller) never depends on which bar you're
 * looking at. A category absent from a given bar still gets an explicit `0`
 * on that row's key — never `undefined` — so `StackedBarChart` renders it as
 * a real (empty) segment rather than collapsing the stack.
 */
export function buildTestRunsTrendChartData(points: TestRunsTrendPoint[]): {
  rows: TestRunsTrendChartRow[];
  categoryKeys: FailureDiagnosticCategory[];
  foldedCategories: FailureDiagnosticCategory[];
} {
  const totalByCategory = new Map<FailureDiagnosticCategory, number>();
  for (const point of points) {
    for (const category of FAILURE_DIAGNOSTIC_CATEGORIES) {
      const value = point.failedByCategory[category] ?? 0;
      if (value <= 0) continue;
      totalByCategory.set(category, (totalByCategory.get(category) ?? 0) + value);
    }
  }

  // Highest volume first; ties broken by canonical FAILURE_DIAGNOSTIC_CATEGORIES
  // order so the ranking is fully deterministic (not dependent on Map
  // iteration or input order).
  const ranked = FAILURE_DIAGNOSTIC_CATEGORIES.filter(
    (category) => (totalByCategory.get(category) ?? 0) > 0
  ).sort((a, b) => (totalByCategory.get(b) ?? 0) - (totalByCategory.get(a) ?? 0));

  const categoryKeys = ranked.slice(0, MAX_DIAGNOSIS_CATEGORIES);
  const foldedCategories = ranked.slice(MAX_DIAGNOSIS_CATEGORIES);

  const rows = points.map((point): TestRunsTrendChartRow => {
    const row: TestRunsTrendChartRow = {
      kind: point.kind,
      executionId: point.executionId,
      date: formatTrendDate(point.startedAt),
      runTargetKey: point.runTarget.key,
      runTargetLabel: point.runTarget.label,
      status: point.status,
      totalWorkflows: point.totalWorkflows,
      passed: point.passed,
      flaky: point.flaky,
      undiagnosed: point.failedByCategory[UNDIAGNOSED_KEY] ?? 0,
      otherDiagnoses: 0,
      otherDiagnosesBreakdown: {},
      other: point.other,
      placeholder: 0,
    };
    for (const category of categoryKeys) {
      row[category] = point.failedByCategory[category] ?? 0;
    }

    const breakdown: Partial<Record<FailureDiagnosticCategory, number>> = {};
    let foldedTotal = 0;
    for (const category of foldedCategories) {
      const value = point.failedByCategory[category] ?? 0;
      if (value <= 0) continue;
      breakdown[category] = value;
      foldedTotal += value;
    }
    row.otherDiagnoses = foldedTotal;
    row.otherDiagnosesBreakdown = breakdown;

    return row;
  });

  return { rows, categoryKeys, foldedCategories };
}

/**
 * Left-pads `rows` to {@link TREND_SLOT_COUNT} with empty placeholder slots, so
 * the bars keep a constant width and the newest run always sits at the right
 * edge. Returns `rows` unchanged once there are at least that many.
 *
 * Placeholders carry an empty `executionId`, which is what the chart keys its
 * click and tooltip handling off — they are inert marks, not zero-valued runs.
 */
export function padTrendRowsToSlots(
  rows: TestRunsTrendChartRow[],
  categoryKeys: FailureDiagnosticCategory[]
): TestRunsTrendChartRow[] {
  if (rows.length >= TREND_SLOT_COUNT) return rows;

  const placeholders = Array.from(
    { length: TREND_SLOT_COUNT - rows.length },
    (_unused, index): TestRunsTrendChartRow => {
      const row: TestRunsTrendChartRow = {
        kind: null,
        executionId: '',
        // Recharts keys categories by `xDataKey`; duplicate values collapse
        // into one bar, so every placeholder needs a distinct `date`.
        date: `placeholder-${index}`,
        runTargetKey: '',
        runTargetLabel: '',
        status: '',
        totalWorkflows: 0,
        passed: 0,
        flaky: 0,
        undiagnosed: 0,
        otherDiagnoses: 0,
        otherDiagnosesBreakdown: {},
        other: 0,
        placeholder: 1,
      };
      for (const category of categoryKeys) row[category] = 0;
      return row;
    }
  );

  return [...placeholders, ...rows];
}
